import { useEffect, useState } from 'react';
import {
  CreateClientAccountReceiveOnlyRequest,
  CreateClientAccountStandardRequest,
  EditClientAccountRequest,
  ClientAccount,
  ClientAccountListRequest,
  PaginationMetaData,
} from 'vopay';
import axios from 'axios';
import vopayApi from '@/services/api.config';

const { api } = vopayApi;

type CreateClientAccountRequest = CreateClientAccountStandardRequest | CreateClientAccountReceiveOnlyRequest;

export default function useClientAccounts() {
  const source = axios.CancelToken.source();
  const [pending, setPending] = useState(false);
  const [clientAccount, setClientAccount] = useState<ClientAccount | null>(null);
  const [clientAccounts, setClientAccounts] = useState<ClientAccount[]>([]);

  const [pagination, setPagination] = useState<PaginationMetaData>({
    TotalRecords: 0,
    Limit: 0,
    Offset: 0,
  });

  useEffect(() => {
    return () => source.cancel();
  }, []);

  async function createClientAccount(req: CreateClientAccountRequest, receiveOnly: boolean) {
    try {
      setPending(true);

      const res = await (receiveOnly ? api.createClientAccountReceiveOnly(req) : api.createClientAccountStandard(req));

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return {
        clientAccountID: res.ClientAccountID,
      };
    } finally {
      setPending(false);
    }
  }

  async function fetchClientAccount(clientAccountID: string) {
    try {
      setPending(true);

      const res = await api.fetchClientAccountList(
        {
          ClientAccountID: clientAccountID,
        },
        source,
      );

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      const clientAccount = res.ClientAccounts.find((a) => a.ClientAccountID === clientAccountID) || null;
      setClientAccount(clientAccount);

      return clientAccount;
    } finally {
      setPending(false);
    }
  }

  async function fetchClientAccounts(params: ClientAccountListRequest) {
    try {
      setPending(true);
      const res = await api.fetchClientAccountList(params, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      setClientAccounts(res.ClientAccounts);
      setPagination(res.PaginationMetaData);

      return res;
    } finally {
      setPending(false);
    }
  }

  async function editClientAccount(params: EditClientAccountRequest) {
    try {
      setPending(true);
      const res = await api.editClientAccount(params, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return res;
    } finally {
      setPending(false);
    }
  }

  return {
    pending,
    createClientAccount,
    clientAccount,
    fetchClientAccount,
    clientAccounts,
    fetchClientAccounts,
    editClientAccount,
    pagination,
  };
}
